import { useMemo } from "react";
import apiClient from "./apiClient";
import { Camera, CAMERA_TYPES } from "../state/types";
import { stripUnitSuffix } from "../utils/systemCode";

// Exterior HTTP cameras (Axis, BWI) ship with fixed factory credentials.
// They're injected here so the form doesn't have to ask for them.
const EXTERIOR_HTTP_USERNAME = "root";
const EXTERIOR_HTTP_PASSWORD = "Blackwidow375";
const EXTERIOR_HTTP_BRANDS = new Set(["axis", "bwi"]);

const LEGACY_TOP_LEVEL_CREDENTIAL_PATHS = ["username", "password"];

const parseCameraPayload = (payload: unknown): Camera[] => {
  if (Array.isArray(payload)) {
    return payload as Camera[];
  }

  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload);
      return Array.isArray(parsed) ? (parsed as Camera[]) : [];
    } catch (error) {
      console.error("Failed to parse camera payload:", error);
      return [];
    }
  }

  return [];
};

const serializeCamera = (camera: Camera, systemCode: string) => {
  const normalizedSystemCode = stripUnitSuffix(systemCode);
  const cameraId = camera.cameraId ?? camera.id;
  const cameraType = camera.type ?? camera.camera_type;
  const brand = (camera.brand || "").toLowerCase();
  const ipAddress = camera.ip_address ?? camera.http_config?.ip_address ?? "";
  const queryParams =
    camera.query_params !== undefined ? camera.query_params : camera.http_config?.query_params ?? null;

  const isExteriorHttp =
    cameraType === CAMERA_TYPES.EXTERIOR && EXTERIOR_HTTP_BRANDS.has(brand);

  const username = isExteriorHttp
    ? EXTERIOR_HTTP_USERNAME
    : camera.username ?? camera.http_config?.username ?? "";
  const password = isExteriorHttp
    ? EXTERIOR_HTTP_PASSWORD
    : camera.password ?? camera.http_config?.password ?? "";

  const order = Number(camera.order ?? camera.sort_position ?? camera.camera_position ?? 0) || 0;

  // Strip the duplicated top-level credential fields; http_config is the canonical source.
  const { username: _droppedUsername, password: _droppedPassword, ...rest } = camera;

  return {
    ...rest,
    systemCode: normalizedSystemCode,
    cameraId,
    id: cameraId,
    type: cameraType,
    camera_type: cameraType,
    order,
    sort_position: order,
    ip_address: ipAddress,
    query_params: queryParams,
    capture_protocol: brand === "axis" ? "http" : "mqtt",
    http_config: {
      ...(camera.http_config || {}),
      ip_address: ipAddress,
      username,
      password,
      query_params: queryParams,
      has_credentials: Boolean(username || password),
    },
    desired_settings: {
      ...(camera.desired_settings || {}),
      skip_ricoh_while_charging: Boolean(camera.skip_ricoh_while_charging),
    },
  };
};

export const useCameraApi = () => {
  return useMemo(() => {
    return {
      listCamerasApi: async (systemCode: string): Promise<Camera[]> => {
        const { data } = await apiClient.post("/camera", {
          system_code: stripUnitSuffix(systemCode),
        });

        return parseCameraPayload(data);
      },

      createCameraApi: async (camera: Camera, systemCode: string) => {
        const payload = serializeCamera(camera, systemCode);

        await apiClient.post("/camera/create", payload);
        return payload as unknown as Camera;
      },

      updateCameraApi: async (camera: Camera, systemCode: string) => {
        const payload = serializeCamera(camera, systemCode);

        // Tell the backend to drop the legacy top-level credential fields if they
        // still exist on the stored record from previous writes.
        await apiClient.put("/camera", {
          ...payload,
          removePaths: LEGACY_TOP_LEVEL_CREDENTIAL_PATHS,
        });
        return payload as unknown as Camera;
      },

      batchUpdateCamerasApi: async (cameras: Camera[], systemCode: string) => {
        const payload = cameras.map((camera) => ({
          ...serializeCamera(camera, systemCode),
          removePaths: LEGACY_TOP_LEVEL_CREDENTIAL_PATHS,
        }));

        await apiClient.put("/camera/batch", {
          systemCode: stripUnitSuffix(systemCode),
          cameras: payload,
        });

        return payload as unknown as Camera[];
      },

      deleteCameraApi: async (cameraId: string | number, systemCode: string) => {
        await apiClient.delete("/camera", {
          data: {
            cameraId,
            systemCode: stripUnitSuffix(systemCode),
          },
        });
      },
    };
  }, []);
};
